import bindAll from 'lodash.bindall';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
import React from 'react';
import {intlShape, injectIntl} from 'react-intl';

import {connect} from 'react-redux';
import {openBackdropLibrary} from '../reducers/modals';
import {activateTab, COSTUMES_TAB_INDEX} from '../reducers/editor-tab';
import {setHoveredSprite} from '../reducers/hovered-target';
import DragConstants from '../lib/drag-constants';
import DropAreaHOC from '../lib/drop-area-hoc.jsx';
import {emptyCostume} from '../lib/empty-assets';
import sharedMessages from '../lib/shared-messages';
import {fetchCode} from '../lib/backpack-api';

import StageSelectorComponent from '../components/stage-selector/stage-selector.jsx';

import backdropLibraryContent from '../lib/libraries/backdrops.json';
import {handleFileUpload, costumeUpload} from '../lib/file-uploader.js';
import {getOssSign, upload, textCheck, toCdnUrl} from '../lib/services/scratch-ser';
import axios from '../lib/services';
import swal from '../lib/swal';
import {queryParams, getUserinfo} from '../lib/services/query-params';
import {getHashParams} from '../lib/query-string';

import {uploadBackdropLimitSize, uploadExceedAlert} from '../lib/upload-limit';

const {role} = queryParams;
const workStatusQuery = getHashParams('workStatus') || queryParams.workStatus;

const getCheckStatus = result => {
    if (result && result.data && result.data.data && result.data.data.status !== undefined) {
        return result.data.data.status;
    }
    if (result && result.data && result.data.status !== undefined) {
        return result.data.status;
    }
    return null;
};

const pollCheck = async params => {
    const startTime = Date.now();
    const timeout = 300000;
    while (Date.now() - startTime < timeout) {
        const result = await axios.post(`${API_BASE_URL}/core/safeCheck/guardian/check`, params);
        const status = getCheckStatus(result);
        if (status === 3 || status === 4 || status === 5) return result;
        if (status !== 1 && status !== 2) return result;
        await new Promise(resolve => window.setTimeout(resolve, 3000));
    }
    throw new Error('guardian/check timeout');
};

const getOssMd5 = async url => {
    try {
        const res = await fetch(url, {method: 'HEAD'});
        const etag = res.headers.get('ETag');
        return etag ? etag.replace(/["']/g, '').toLowerCase() : null;
    } catch (error) {
        return null;
    }
};

const dragTypes = [
    DragConstants.COSTUME,
    DragConstants.SOUND,
    DragConstants.BACKPACK_COSTUME,
    DragConstants.BACKPACK_SOUND,
    DragConstants.BACKPACK_CODE
];

const DroppableStage = DropAreaHOC(dragTypes)(StageSelectorComponent);

class StageSelector extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClick',
            'handleNewBackdrop',
            'handleSurpriseBackdrop',
            'handleEmptyBackdrop',
            'addBackdropFromLibraryItem',
            'handleFileUploadClick',
            'handleBackdropUpload',
            'handleMouseEnter',
            'handleMouseLeave',
            'handleDrop',
            'setFileInput'
        ]);
    }
    addBackdropFromLibraryItem (item) {
        const vmBackdrop = {
            name: item.name,
            md5: item.md5,
            rotationCenterX: item.info[0] && item.info[0] / 2,
            rotationCenterY: item.info[1] && item.info[1] / 2,
            bitmapResolution: item.info.length > 2 ? item.info[2] : 1,
            skinId: null
        };
        this.handleNewBackdrop(vmBackdrop);
    }
    handleClick () {
        this.props.onSelect(this.props.id);
    }
    handleNewBackdrop (backdrop) {
        this.props.vm.addBackdrop(backdrop.md5, backdrop).then(() =>
            this.props.onActivateTab(COSTUMES_TAB_INDEX));
    }
    handleSurpriseBackdrop () {
        // @todo should this not add a backdrop you already have?
        const item = backdropLibraryContent[Math.floor(Math.random() * backdropLibraryContent.length)];
        this.addBackdropFromLibraryItem(item);
    }
    handleEmptyBackdrop () {
        this.handleNewBackdrop(emptyCostume(this.props.intl.formatMessage(sharedMessages.backdrop, {index: 1})));
    }
    handleBackdropUpload (e) {
        const storage = this.props.vm.runtime.storage;
        const fileInput = e.target;
        const uploadFile = fileInput.files && fileInput.files[0];
        if (!uploadFile) return;
        const actualBackdropSize = (uploadFile.size / Math.pow(1024, 2)).toFixed(2);
        if (actualBackdropSize >= uploadBackdropLimitSize) {
            fileInput.value = null;
            uploadExceedAlert(uploadBackdropLimitSize, actualBackdropSize);
            return false;
        }
        const readAndUploadBackdrop = () => {
            handleFileUpload(fileInput, (buffer, fileType, fileName) => {
                costumeUpload(buffer, fileType, fileName, storage, this.handleNewBackdrop);
            });
        };
        if (!workStatusQuery || !uploadFile.type.startsWith('image/')) {
            readAndUploadBackdrop();
            return;
        }
        const imageFormat = uploadFile.name && uploadFile.name.lastIndexOf('.') > -1 ?
            uploadFile.name.slice(uploadFile.name.lastIndexOf('.')).toLowerCase() : '';
        getOssSign({
            params: [{
                type: 2,
                fileName: uploadFile.name
            }]
        })
            .then(response => {
                const signData = response.data.data[0].sign;
                const businessId = response.data.data[0].savePath || signData.fileName;
                return upload(uploadFile, signData).then(uploadRes => ({
                    signData,
                    businessId,
                    uploadRes
                }));
            })
            .then(async ({signData, businessId, uploadRes}) => {
                const {id: authorId, realName: authorName} = getUserinfo() || {};
                const url = toCdnUrl(signData.dir);
                const fallbackMd5 = ((businessId || signData.fileName || signData.dir || '').split('?')[0].split('/').pop() || '').split('.')[0];
                const etagMd5 = uploadRes && uploadRes.headers && uploadRes.headers.etag ?
                    uploadRes.headers.etag.replace(/['"]/g, '').toLowerCase() : null;
                const md5 = etagMd5 || await getOssMd5(url) || fallbackMd5;
                const textCheckRes = await textCheck({url: uploadFile.name, module: 6});
                if (getCheckStatus(textCheckRes) === 4) {
                    await swal.alert('提示', `素材图片${imageFormat ? `(${imageFormat.replace('.', '').toUpperCase()})` : ''}不符合平台的发布规范，请重新上传`);
                    fileInput.value = null;
                    return;
                }
                const result = await pollCheck({
                    type: 1,
                    url,
                    md5,
                    module: 1,
                    authorId,
                    authorName,
                    role,
                    businessId: businessId || md5
                });
                if (getCheckStatus(result) === 4) {
                    await swal.alert('提示', `素材图片${imageFormat ? `(${imageFormat.replace('.', '').toUpperCase()})` : ''}不符合平台的发布规范，请重新上传`);
                    fileInput.value = null;
                    return;
                }
                readAndUploadBackdrop();
            })
            .catch(error => {
                fileInput.value = null;
                console.error('backdrop image upload check error:', error);
                swal.alert('提示', '图片资源校验失败，请稍后重试');
            });
    }
    handleFileUploadClick () {
        this.fileInput.click();
    }
    handleMouseEnter () {
        this.props.dispatchSetHoveredSprite(this.props.id);
    }
    handleMouseLeave () {
        this.props.dispatchSetHoveredSprite(null);
    }
    handleDrop (dragInfo) {
        if (dragInfo.dragType === DragConstants.COSTUME) {
            this.props.vm.shareCostumeToTarget(dragInfo.index, this.props.id);
        } else if (dragInfo.dragType === DragConstants.SOUND) {
            this.props.vm.shareSoundToTarget(dragInfo.index, this.props.id);
        } else if (dragInfo.dragType === DragConstants.BACKPACK_COSTUME) {
            this.props.vm.addCostume(dragInfo.payload.body, {
                name: dragInfo.payload.name
            }, this.props.id);
        } else if (dragInfo.dragType === DragConstants.BACKPACK_SOUND) {
            this.props.vm.addSound({
                md5: dragInfo.payload.body,
                name: dragInfo.payload.name
            }, this.props.id);
        } else if (dragInfo.dragType === DragConstants.BACKPACK_CODE) {
            fetchCode(dragInfo.payload.bodyUrl)
                .then(blocks => {
                    this.props.vm.shareBlocksToTarget(blocks, this.props.id);
                    this.props.vm.refreshWorkspace();
                });
        }
    }
    setFileInput (input) {
        this.fileInput = input;
    }
    render () {
        const componentProps = omit(this.props, [
            'asset', 'dispatchSetHoveredSprite', 'id', 'intl', 'onActivateTab', 'onSelect']);
        return (
            <DroppableStage
                fileInputRef={this.setFileInput}
                onBackdropFileUpload={this.handleBackdropUpload}
                onBackdropFileUploadClick={this.handleFileUploadClick}
                onClick={this.handleClick}
                onDrop={this.handleDrop}
                onEmptyBackdropClick={this.handleEmptyBackdrop}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                onSurpriseBackdropClick={this.handleSurpriseBackdrop}
                {...componentProps}
            />
        );
    }
}
StageSelector.propTypes = {
    ...StageSelectorComponent.propTypes,
    id: PropTypes.string,
    intl: intlShape.isRequired,
    onSelect: PropTypes.func
};

const mapStateToProps = (state, {asset, id}) => ({
    url: asset && asset.encodeDataURI(),
    vm: state.scratchGui.vm,
    receivedBlocks: state.scratchGui.hoveredTarget.receivedBlocks &&
            state.scratchGui.hoveredTarget.sprite === id,
    raised: state.scratchGui.blockDrag
});

const mapDispatchToProps = dispatch => ({
    onNewBackdropClick: e => {
        e.stopPropagation();
        dispatch(openBackdropLibrary());
    },
    onActivateTab: tabIndex => {
        dispatch(activateTab(tabIndex));
    },
    dispatchSetHoveredSprite: spriteId => {
        dispatch(setHoveredSprite(spriteId));
    }
});

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(StageSelector));
